import { supabase } from './supabase';
import type { EmailAttachment } from './email';

// The blank liability waiver the owner emails out to clients. Stored in
// the private `documents` bucket; its path is remembered in app_settings
// so the "Email waiver" button knows what to attach.

const BUCKET = 'documents';
const SETTING_KEY = 'liability_form_template';

export interface LiabilityTemplate {
  path: string;
  filename: string;
  uploaded_at: string;
}

export async function getLiabilityTemplate(): Promise<LiabilityTemplate | null> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', SETTING_KEY)
    .maybeSingle();
  if (error) throw error;
  const value = data?.value as LiabilityTemplate | undefined;
  return value && value.path ? value : null;
}

export async function uploadLiabilityTemplate(file: File): Promise<LiabilityTemplate> {
  const safeName = file.name.replace(/[^\w.-]+/g, '_').slice(-80) || 'liability-waiver.pdf';
  const path = `liability-waiver/${Date.now()}-${safeName}`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { contentType: file.type || 'application/pdf' });
  if (upErr) throw upErr;

  const previous = await getLiabilityTemplate().catch(() => null);
  const value: LiabilityTemplate = {
    path,
    filename: file.name,
    uploaded_at: new Date().toISOString(),
  };
  const { error: setErr } = await supabase
    .from('app_settings')
    .upsert({ key: SETTING_KEY, value, updated_at: new Date().toISOString() });
  if (setErr) throw setErr;

  // Clean up the old file once the new one is recorded.
  if (previous?.path && previous.path !== path) {
    await supabase.storage
      .from(BUCKET)
      .remove([previous.path])
      .catch(() => {});
  }
  return value;
}

export async function removeLiabilityTemplate(): Promise<void> {
  const current = await getLiabilityTemplate().catch(() => null);
  if (current?.path) {
    await supabase.storage
      .from(BUCKET)
      .remove([current.path])
      .catch(() => {});
  }
  const { error } = await supabase.from('app_settings').delete().eq('key', SETTING_KEY);
  if (error) throw error;
}

export async function templateSignedUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 10);
  if (error || !data?.signedUrl) throw error ?? new Error('Could not create a link.');
  return data.signedUrl;
}

// Turn the stored template into an attachment the function fetches
// server-side (no need to download it into the browser first).
export function templateAttachment(t: LiabilityTemplate): EmailAttachment {
  return { filename: t.filename, bucket: BUCKET, path: t.path };
}
